// src/features/dashboard/components/EmployeeLoggedHoursReport/LoggedHoursHeader.tsx

import {
  LoggedHoursKpiResponse,
} from '../../types/Employee/employeeLoggedHours.types';

import { useDashboardDate } from '../../context/DashboardDateContext';

interface Props {
  kpi?: LoggedHoursKpiResponse | null;
};

/**
 * Format API time.
 *
 * 09:40:12 -> 9h 40m
 * 01:18:00 -> 1h 18m
 * 00:18:00 -> 0h 18m
 */
const formatHoursMinutes = (
  time?: string | null
): string => {
  if (!time) {
    return '--';
  }

  const parts = time.split(':');

  if (parts.length < 2) {
    return time;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return time;
  }

  return `${hours}h ${minutes}m`;
};

/**
 * Parse calendar date without timezone conversion.
 *
 * Supports:
 * 2026-08-13
 * 2026-08-13T00:00:00
 * Date object
 */
const parseCalendarDate = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    return new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate()
    );
  }

  /**
   * Parse YYYY-MM-DD manually.
   *
   * This prevents:
   * 2026-08-12
   *
   * from being converted to the previous day
   * because of UTC timezone conversion.
   */
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})/
  );

  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);

    return new Date(year, month, day);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
};

/**
 * Remove time from date.
 */
const startOfDay = (date: Date): Date => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
};

/**
 * Get calendar day difference.
 *
 *  0  = Today
 * -1  = Yesterday
 * -2  = 2 days ago
 *  1  = Tomorrow
 */
const getDayDifference = (
  selectedDate: Date,
  today: Date
): number => {
  const selected = startOfDay(selectedDate);
  const current = startOfDay(today);

  const millisecondsPerDay =
    24 * 60 * 60 * 1000;

  return Math.round(
    (selected.getTime() - current.getTime()) /
      millisecondsPerDay
  );
};

/**
 * Format full calendar date.
 *
 * Example:
 * 13 Aug 2026
 */
const formatFullDate = (
  date: Date
): string => {
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Get header date label.
 *
 * Today:
 * Today 13 Aug 2026 9h 40m
 *
 * Yesterday:
 * Yesterday 12 Aug 2026 8h 20m
 *
 * Other date:
 * 11 Aug 2026
 */
const getDateLabel = (
  value: string | Date | null | undefined,
  loggedTime?: string | null
): string => {
  let selectedDate = parseCalendarDate(value);

  /**
   * If calendar date is not available,
   * use today's date.
   */
  if (!selectedDate) {
    selectedDate = startOfDay(new Date());
  }

  const today = startOfDay(new Date());

  const difference = getDayDifference(
    selectedDate,
    today
  );

  const fullDate = formatFullDate(
    selectedDate
  );

  const formattedTime =
    formatHoursMinutes(loggedTime);

  /**
   * Today
   */
  if (difference === 0) {
    return `Today ${fullDate} ${formattedTime}`;
  }

  /**
   * Yesterday
   */
  if (difference === -1) {
    return `Yesterday ${fullDate} ${formattedTime}`;
  }

  /**
   * Any other date:
   * Show date only.
   */
  return fullDate;
};

const LoggedHoursHeader = ({
  kpi,
}: Props) => {
  /**
   * Get selected calendar date.
   *
   * toDate is used because the calendar
   * can contain a selected date/range.
   */
  const { toDate } = useDashboardDate();

  const employeeName =
    sessionStorage.getItem('employeeName') ||
    localStorage.getItem('employeeName') ||
    'Employee';

  const businessUnit =
    sessionStorage.getItem('businessUnit') ||
    localStorage.getItem('businessUnit') ||
    '';

  /**
   * Date + logged hours.
   */
  const dateLabel = getDateLabel(
    toDate,
    kpi?.loggedToday
  );

  return (
    <div
      className="
        mb-4
        flex
        flex-wrap
        items-center
        gap-3
      "
    >
      {/* Title */}

      <h1
        className="
          text-[16px]
          font-semibold
          leading-none
          text-[#002B5B]
        "
      >
        Logged Hours
      </h1>

      {/* Separator */}

      <span
        className="
          text-[16px]
          font-medium
          leading-none
          text-[#002B5B]
        "
      >
        ·
      </span>

      {/* Selected Date + Logged Hours */}

      <span
        className="
          text-[16px]
          font-semibold
          leading-none
          text-[#002B5B]
        "
      >
        {dateLabel}
      </span>

      {/* Employee / Business Unit / WTD */}

      <div
        className="
          ml-2
          rounded-full
          bg-[#EEF4FF]
          px-4
          py-2
          text-[12px]
          font-medium
          text-[#3538CD]
        "
      >
        {employeeName}

        {businessUnit && (
          <>
            {' · '}
            {businessUnit}
          </>
        )}

        {toDate && (
          <>
            {' · WTD '}
            {formatFullDate(
              parseCalendarDate(toDate) ??
                new Date()
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoggedHoursHeader;